/**
 * Lab result board logic (alignment map V1-F7-05a LabResult; F7.5): the attach form checked like
 * uc-core's CreateLabReportDto (multipart pdf + resultsFile, reportNo ≤ 40, testedAt date, the
 * sample by its scanned seal), the row flags in plain words and the board's counters. V1 has no
 * second-person Verified step: the releaser reads the PDF.
 */
import type { Tone } from './payoutBoard'

export interface LabForm {
  seal: string
  labOrgId: string
  reportNo: string
  /** yyyy-mm-dd */
  testedAt: string
  pdf: File | null
  results: File | null
}
export type LabFormErrors = Partial<Record<keyof LabForm, string>>

const PDF_MAX = 20 * 1024 * 1024

export function validateLabForm(f: LabForm, today: string): LabFormErrors {
  const e: LabFormErrors = {}
  if (!f.seal.trim()) e.seal = 'Scan or type the seal on the sample.'
  else if (f.seal.trim().length > 64) e.seal = 'A seal is at most 64 characters.'
  if (!f.labOrgId) e.labOrgId = 'No contracted lab is set up.'
  const no = f.reportNo.trim()
  if (!no) e.reportNo = 'Type the report number from the PDF.'
  else if (no.length > 40) e.reportNo = 'At most 40 characters.'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.testedAt)) e.testedAt = 'Pick the test date.'
  else if (f.testedAt > today) e.testedAt = 'The test date cannot be in the future.'
  if (!f.pdf) e.pdf = 'Attach the PDF as received from the lab.'
  else if (!isPdf(f.pdf)) e.pdf = 'This is not a PDF.'
  else if (f.pdf.size > PDF_MAX) e.pdf = 'The PDF is larger than 20 MB.'
  if (!f.results) e.results = 'Attach the lab’s results file (CSV or spreadsheet).'
  else if (f.results.size > PDF_MAX) e.results = 'The results file is larger than 20 MB.'
  return e
}

const isPdf = (f: { name: string; type: string }) =>
  f.type === 'application/pdf' || /\.pdf$/i.test(f.name)

/** Data rows in a CSV results file (header excluded, blank lines skipped). */
export function csvRowCount(text: string): number {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '')
  return Math.max(0, lines.length - 1)
}

/** A row's flag in the board's words: Pass, or why it is not. */
export function flagState(flag: string): { tone: Tone; text: string } {
  switch (flag) {
    case 'PASS':
      return { tone: 'ok', text: 'Pass' }
    case 'DETECTED':
      return { tone: 'bad', text: 'Detected above LOQ' }
    case 'OVER_LIMIT':
      return { tone: 'bad', text: 'Over the limit' }
    case 'LOQ_TOO_HIGH':
      return { tone: 'bad', text: 'LOQ above the claim' }
    default:
      return { tone: 'idle', text: flag }
  }
}

/** The report's overall verdict in the board's words. */
export function overallState(overall: string | null | undefined): { tone: Tone; text: string } {
  switch (overall) {
    case 'PASS':
      return { tone: 'ok', text: 'Pass' }
    case 'FAIL':
      return { tone: 'bad', text: 'Fail' }
    case 'INCOMPLETE':
      return { tone: 'bad', text: 'Incomplete: a required panel is missing' }
    case 'NOT_ACCEPTED':
      return {
        tone: 'bad',
        text: 'Not accepted: the lab’s accreditation had lapsed on the test date',
      }
    default:
      return { tone: 'wait', text: overall ?? 'Pending' }
  }
}

/** "Not detected" is always shown with its LOQ, never as 0 or nil. */
export function resultText(value: string | null, loq: string, uom: string): string {
  return value === null || value === '' ? `Not detected (LOQ ${loq} ${uom})` : `${value} ${uom}`
}

export interface ReportLite {
  overall: string | null
  status: string
  rowsNotPass: number
  enteredAt: string | null
}

/** Board counters: attached this week (IST Monday on), incomplete, not accepted, held for re-test. */
export function reportCounters(list: ReadonlyArray<ReportLite>, now = new Date()) {
  const monday = istMonday(now)
  return {
    thisWeek: list.filter((r) => r.enteredAt && r.enteredAt >= monday).length,
    incomplete: list.filter((r) => r.overall === 'INCOMPLETE').length,
    notAccepted: list.filter((r) => r.overall === 'NOT_ACCEPTED').length,
    retest: list.filter(
      (r) => r.overall === 'FAIL' && r.status !== 'SUPERSEDED' && r.status !== 'REJECTED',
    ).length,
  }
}

/** ISO instant of this week's Monday 00:00 IST. */
export function istMonday(now: Date): string {
  const ist = new Date(now.getTime() + 330 * 60_000)
  const dow = (ist.getUTCDay() + 6) % 7
  const day = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate() - dow)
  return new Date(day - 330 * 60_000).toISOString()
}

/** The sealed sample a scanned seal belongs to (case and spaces ignored). */
export function findBySeal<T extends { sealPayload: string | null }>(
  samples: ReadonlyArray<T>,
  seal: string,
): T | null {
  const s = seal.trim().toUpperCase()
  if (!s) return null
  return samples.find((x) => (x.sealPayload ?? '').trim().toUpperCase() === s) ?? null
}
